// SpinningLogo.tsx — v3 · Crystal Hexagon · cyan/teal brand palette (no purple)
import React from 'react'

interface SpinningLogoProps {
  size?: 'sm' | 'md' | 'lg' | 'xl'
  showText?: boolean
  textSize?: string
}

const SpinningLogo: React.FC<SpinningLogoProps> = ({
  size = 'md',
  showText = true,
  textSize = 'text-xl',
}) => {
  const dims = {
    sm:  { container: 36,  svg: 36,  text: 'text-base' },
    md:  { container: 52,  svg: 52,  text: 'text-xl' },
    lg:  { container: 76,  svg: 76,  text: 'text-2xl' },
    xl:  { container: 112, svg: 112, text: 'text-4xl' },
  }
  const d = dims[size]
  const c = d.container / 2      // centre point of SVG
  const R = c * 0.88             // outer hex radius

  // Hexagon points helper
  const hexPoints = (cx: number, cy: number, r: number, offsetDeg = 0) => {
    return Array.from({ length: 6 }, (_, i) => {
      const angle = ((i * 60 + offsetDeg) * Math.PI) / 180
      return `${cx + r * Math.cos(angle)},${cy + r * Math.sin(angle)}`
    }).join(' ')
  }

  const outerHex   = hexPoints(c, c, R, -90)
  const middleHex  = hexPoints(c, c, R * 0.65, -90)
  const innerHex   = hexPoints(c, c, R * 0.38, -90)

  // Star-burst spokes
  const spokeCount = 6
  const spokes = Array.from({ length: spokeCount }, (_, i) => {
    const angle = ((i * 60 - 90) * Math.PI) / 180
    return {
      x1: c + (R * 0.40) * Math.cos(angle),
      y1: c + (R * 0.40) * Math.sin(angle),
      x2: c + (R * 0.82) * Math.cos(angle),
      y2: c + (R * 0.82) * Math.sin(angle),
    }
  })

  const sId = `lg-${size}` // unique id prefix for gradients

  return (
    <div className="flex items-center gap-3 select-none">
      {/* ─── Animated SVG mark ──────────────────────────── */}
      <div style={{ width: d.container, height: d.container, flexShrink: 0, position: 'relative' }}>
        <svg
          width={d.svg}
          height={d.svg}
          viewBox={`0 0 ${d.container} ${d.container}`}
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{ overflow: 'visible' }}
        >
          <defs>
            {/* Cyan → Teal gradient */}
            <linearGradient id={`${sId}-g1`} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%"   stopColor="#22d3ee" />
              <stop offset="100%" stopColor="#0d9488" />
            </linearGradient>
            {/* Teal → Cyan soft */}
            <linearGradient id={`${sId}-g2`} x1="1" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor="#0d9488" stopOpacity="0.7" />
              <stop offset="100%" stopColor="#22d3ee" stopOpacity="0.3" />
            </linearGradient>
            {/* Core glow radial */}
            <radialGradient id={`${sId}-core`} cx="50%" cy="50%" r="50%">
              <stop offset="0%"   stopColor="#22d3ee" stopOpacity="0.95" />
              <stop offset="70%"  stopColor="#0d9488" stopOpacity="0.80" />
              <stop offset="100%" stopColor="#164e63" stopOpacity="0.60" />
            </radialGradient>
            {/* Glow filter */}
            <filter id={`${sId}-glow`} x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="1.5" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            <filter id={`${sId}-softglow`} x="-60%" y="-60%" width="220%" height="220%">
              <feGaussianBlur stdDeviation="2.5" />
            </filter>
          </defs>

          {/* ── Outer ambient glow ring ─── (not animated, just ambience) */}
          <polygon
            points={outerHex}
            fill="none"
            stroke="#22d3ee"
            strokeWidth="0.5"
            strokeOpacity="0.15"
            filter={`url(#${sId}-softglow)`}
          />

          {/* ── Outer spinning hexagon ─── slow CW */}
          <polygon
            points={outerHex}
            fill="none"
            stroke={`url(#${sId}-g1)`}
            strokeWidth={size === 'xl' ? 1.2 : 0.9}
            strokeDasharray="4 3"
            strokeLinecap="round"
            style={{ animation: 'spin 18s linear infinite', transformOrigin: `${c}px ${c}px` }}
          />

          {/* ── Spokes from middle to outer ─── reverse spin */}
          <g style={{ animation: 'spin 12s linear infinite reverse', transformOrigin: `${c}px ${c}px` }}>
            {spokes.map((s, i) => (
              <line
                key={i}
                x1={s.x1} y1={s.y1} x2={s.x2} y2={s.y2}
                stroke="#22d3ee"
                strokeWidth="0.6"
                strokeOpacity="0.35"
                strokeLinecap="round"
              />
            ))}
          </g>

          {/* ── Middle hexagon ─── reverse spin */}
          <polygon
            points={middleHex}
            fill="none"
            stroke={`url(#${sId}-g2)`}
            strokeWidth={size === 'xl' ? 1.0 : 0.75}
            style={{ animation: 'spin 9s linear infinite reverse', transformOrigin: `${c}px ${c}px` }}
          />

          {/* ── Orbit dot on outer hex ─── */}
          <g style={{ animation: 'spin 18s linear infinite', transformOrigin: `${c}px ${c}px` }}>
            <circle
              cx={c + R}
              cy={c}
              r={size === 'sm' ? 2.2 : size === 'md' ? 3 : size === 'lg' ? 4 : 5.5}
              fill="#22d3ee"
              filter={`url(#${sId}-glow)`}
            />
          </g>

          {/* ── Orbit dot on middle hex ─── */}
          <g style={{ animation: 'spin 9s linear infinite reverse', transformOrigin: `${c}px ${c}px` }}>
            <circle
              cx={c + R * 0.65}
              cy={c}
              r={size === 'sm' ? 1.5 : size === 'md' ? 2.2 : size === 'lg' ? 3 : 4}
              fill="#0d9488"
              filter={`url(#${sId}-glow)`}
            />
          </g>

          {/* ── Inner solid hex core ─── */}
          <polygon
            points={innerHex}
            fill={`url(#${sId}-core)`}
            stroke="rgba(34,211,238,0.60)"
            strokeWidth={size === 'xl' ? 1.2 : 0.8}
            filter={`url(#${sId}-glow)`}
            style={{ animation: 'spin 24s linear infinite', transformOrigin: `${c}px ${c}px` }}
          />

          {/* ── Centre: Signal bars icon ─── */}
          {(() => {
            const bw  = size === 'sm' ? 2.2 : size === 'md' ? 3.2 : size === 'lg' ? 4.5 : 6.5
            const gap = bw * 0.55
            const total = 3 * bw + 2 * gap
            const startX = c - total / 2
            const baseY  = c + (size === 'sm' ? 3 : size === 'md' ? 4.5 : size === 'lg' ? 6.5 : 9)
            const heights = [
              size === 'sm' ? 4 : size === 'md' ? 6 : size === 'lg' ? 8.5 : 12,
              size === 'sm' ? 6 : size === 'md' ? 9 : size === 'lg' ? 13 : 18,
              size === 'sm' ? 4 : size === 'md' ? 6 : size === 'lg' ? 8.5 : 12,
            ]
            return heights.map((h, i) => (
              <rect
                key={i}
                x={startX + i * (bw + gap)}
                y={baseY - h}
                width={bw}
                height={h}
                rx={bw / 3}
                fill="white"
                fillOpacity={i === 1 ? 1 : 0.75}
              />
            ))
          })()}
        </svg>
      </div>

      {/* ─── Brand text ─────────────────────────────────── */}
      {showText && (
        <span
          className={`font-black ${textSize}`}
          style={{
            fontFamily: 'var(--font-display)',
            letterSpacing: '0.08em',
            background: 'linear-gradient(120deg, #22d3ee 0%, #0d9488 55%, #22d3ee 100%)',
            backgroundSize: '200% 100%',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            animation: 'logoShimmer 4s ease-in-out infinite',
          }}
        >
          LARXNET
        </span>
      )}
    </div>
  )
}

export default SpinningLogo
